export type MemberRole = 'user' | 'member' | 'organizer' | 'admin';

export interface Member {
  uid: string;
  name: string;
  email: string;
  phone?: string;
  lmNumber?: string;
  role: MemberRole;
  isAdmin: boolean;
  photoURL?: string;
  createdAt: string;
}

export interface Event {
  id: string;
  title: string;
  description: string | null;
  date: string;
  location: string | null;
  /**
   * Optional http(s) link for the venue, such as a Google Maps link.
   * Null when unset. The venue name itself is the link.
   */
  venueMapUrl?: string | null;
  image: string | null;
  /**
   * Landscape card image. Null when unset.
   * Same shape as `image`: a public path such as `/api/public/events/:slug/card-image?v=…`,
   * or a static URL. Clients should treat the value as opaque.
   */
  cardImage?: string | null;
  status: EventStatus;
  ctaMode: EventCtaMode;
  ctaLabel?: string;
  ctaUrl?: string;
  coordinator?: string;
  specialMention?: string;
  /** Past-event card link. Null when there is no details button. */
  detailsUrl?: string | null;
  detailsLabel?: string | null;
  galleryAlbumId?: string | null;
  /** Public album slug. Null when the event has no album. */
  galleryAlbumSlug?: string | null;
  participantCount: number;
  isParticipating?: boolean;
  participantCountOverride?: number;
  participants?: EventParticipantDetail[];
  createdBy: string;
  createdAt: string;
  slug: string;
  regPrefix?: string;
  publishState: EventPublishState;
  visibility: EventVisibility;
  /** Effective: admin toggle on, published, before deadline, not full. False when registration is not required. */
  registrationOpen: boolean;
  registrationClosedReason: RegistrationClosedReason;
  /**
   * True for drives people join without signing up.
   * `registrationOpen` stays false and `registrationClosedReason` is `not_required`.
   */
  registrationNotRequired?: boolean;
  /**
   * True while the event is visible and signups are not open yet.
   * `registrationOpen` stays false and `registrationClosedReason` is `coming_soon`.
   */
  registrationComingSoon?: boolean;
  /** Admin toggle as stored (admin payloads only) */
  registrationToggle?: boolean;
  /**
   * WhatsApp invite when the admin has marked it public.
   * Null when the link stays on the registration confirmation only.
   */
  whatsappGroupUrl?: string | null;
  /** True when `whatsappGroupUrl` is meant to show without registering. */
  whatsappGroupPublic?: boolean;
  deadlineAt?: string | null;
  capacity?: number | null;
  remaining?: number | null;
  feeMember?: number | null;
  feeNonMember?: number | null;
  upiId?: string | null;
  upiQrUrl?: string | null;
  formOptions: EventFormOptions;
  myRegistration?: MyEventRegistration | null;
  registrationCount?: number;
  registrationSummary?: RegistrationSummary;
  /**
   * Places people can leave items. Absent on older API responses; treat that as none.
   */
  dropPoints?: EventDropPoint[];
  /** When true, the public pickup form is open. Absent means off. */
  pickupEnabled?: boolean;
  /** Admin payloads. When true, a WhatsApp reminder goes out the day before. */
  whatsappReminderEnabled?: boolean;
  /** Admin event list only. Absent on member and public payloads. */
  pickupRequestCount?: number;
  /**
   * Saved position in the admin reorder list. Null when the event still
   * follows the date. Admin payloads only.
   */
  displayOrder?: number | null;
}

export interface EventDropPoint {
  /** Optional short name, such as a neighbourhood. */
  label: string | null;
  address: string;
  /** https link on google.com/maps, maps.app.goo.gl, or goo.gl/maps. */
  mapsUrl: string | null;
}

export type PickupRequestStatus = 'new' | 'scheduled' | 'picked_up' | 'cancelled';

export interface EventPickupRequest {
  id: string;
  eventId: string;
  name: string;
  phone: string;
  address: string;
  status: PickupRequestStatus;
  createdAt: string;
}

export type EventStatus = 'upcoming' | 'active' | 'past';
export type EventCtaMode = 'none' | 'internal' | 'external';
export type EventPublishState = 'draft' | 'published' | 'archived';
export type EventVisibility = 'public' | 'members';
export type RegistrationMemberType = 'member' | 'non_member';
export type RegistrationGender = 'male' | 'female' | 'other';
export type RegistrationPaymentStatus = 'not_required' | 'pending' | 'verified' | 'rejected';
export type RegistrationSource = 'public' | 'app';
export type RegistrationClosedReason =
  | 'closed'
  | 'deadline'
  | 'full'
  | 'archived'
  | 'not_required'
  | 'coming_soon'
  | null;

export interface EventFormOptions {
  collectDob: boolean;
  collectGender: boolean;
  memberChoice: boolean;
  paymentRequired: boolean;
  /** Team size including the registrant; null = individual registration */
  teamSize: { min: number; max: number } | null;
  /** Label of an optional free-text question; null = not asked */
  notesQuestion: string | null;
  notesRequired: boolean;
  minAgeYears: number | null;
  /** YYYY-MM-DD; defaults to the event date when unset */
  ageAsOn: string | null;
  paymentInstructions: string | null;
  whatsappGroupUrl: string | null;
  /** When true, event cards and details show a Join WhatsApp group button. */
  whatsappGroupPublic: boolean;
  /**
   * Sections left on before they have any data. Omitted when every open section
   * is already implied by the fields above. Unknown keys are dropped.
   */
  modules?: Partial<Record<
    'registration' | 'fees' | 'questions' | 'dropPoints' | 'whatsapp' | 'cta' | 'highlights' | 'roster',
    boolean
  >>;
}

export interface RegistrationSummary {
  total: number;
  pending: number;
  verified: number;
  rejected: number;
  notRequired: number;
}

export interface MyEventRegistration {
  id: string;
  regNumber: string;
  paymentStatus: RegistrationPaymentStatus;
  memberType: RegistrationMemberType | null;
  amountDue: number | null;
  createdAt: string;
}

export interface EventRegistration {
  id: string;
  eventId: string;
  regNumber: string;
  teamName?: string | null;
  seq: number;
  source: RegistrationSource;
  memberUid: string | null;
  name: string;
  phone: string;
  email: string;
  dob: string | null;
  gender: RegistrationGender | null;
  memberType: RegistrationMemberType | null;
  teammates: string[];
  notes: string | null;
  amountDue: number | null;
  hasPaymentScreenshot: boolean;
  /** LM number when this registration is tied to a member account. */
  lmNumber?: string | null;
  paymentStatus: RegistrationPaymentStatus;
  createdAt: string;
}

export interface EventParticipantDetail {
  uid: string;
  name: string;
  email: string;
}

export type QuizCampaignAudience = 'members_only' | 'all_signed_in';
export type CampaignPhase = 'upcoming' | 'active' | 'ended';
export type QuizTimerMode = 'per_round' | 'per_question';

export interface QuizCampaign {
  id: string;
  slug: string;
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  published: boolean;
  audience: QuizCampaignAudience;
  timerSeconds: number;
  roundTimerSeconds: number;
  questionTimerSeconds?: number | null;
  timerMode: QuizTimerMode;
  sponsorNames: string[];
  sponsorLogoUrl?: string | null;
  sponsorLinkUrl?: string | null;
  sponsorTagline?: string | null;
}

export interface QuizSession {
  id: string;
  label: string;
  opensAt: string;
  closesAt: string;
  sortOrder: number;
}

export type QuizQuestionMediaKind = 'image' | 'audio' | 'video';
export type QuizQuestionMediaSource = 'url' | 'static';
export type QuizQuestionKind = 'scored' | 'survey';

export interface QuizQuestionMedia {
  kind: QuizQuestionMediaKind;
  source: QuizQuestionMediaSource;
  value: string;
}

export interface QuizQuestionForPlay {
  id: number;
  kind: QuizQuestionKind;
  question: string;
  options: string[];
  points: number;
  media: QuizQuestionMedia | null;
}

export interface QuizQuestionAdmin extends QuizQuestionForPlay {
  correctAnswer: number;
  position: number;
}

export interface QuizSurveyQuestion extends Omit<QuizQuestionForPlay, 'points'> {
  kind: 'survey';
}

export interface QuizAttemptResult {
  status: 'submitted';
  totalPoints: number;
  maxPoints: number;
  durationSeconds: number;
  submittedAt: string;
}

export interface CampaignLeaderboardEntry {
  rank: number;
  uid: string;
  name: string;
  totalPoints: number;
  totalDurationSeconds: number;
  sessionsCompleted: number;
  earliestSubmittedAt?: string;
}

export interface CampaignRoundLeaderboard {
  session: QuizSession;
  leaderboard: CampaignLeaderboardEntry[];
}

export interface QuizCampaignSummary {
  ok: boolean;
  quizNavVisible: boolean;
  leaderboardNavVisible: boolean;
  phase: CampaignPhase | null;
  campaign: QuizCampaign | null;
  currentSession: QuizSession | null;
  nextSession: QuizSession | null;
  participantCount: number;
  top5: CampaignLeaderboardEntry[];
}

export interface QuizCampaignLeaderboardResponse {
  ok: boolean;
  campaign: QuizCampaign | null;
  phase: CampaignPhase | null;
  session: QuizSession | null;
  leaderboard: CampaignLeaderboardEntry[];
  roundLeaderboards: CampaignRoundLeaderboard[];
  overallLeaderboard: CampaignLeaderboardEntry[];
}

export interface QuizPlayResponse {
  ok: boolean;
  state: 'unavailable' | 'waiting' | 'ready' | 'completed';
  campaign?: QuizCampaign;
  session?: QuizSession;
  nextSession?: QuizSession | null;
  timerSeconds?: number;
  timerMode?: QuizTimerMode;
  roundTimerSeconds?: number;
  questionTimerSeconds?: number | null;
  serverTime: string;
  questions?: QuizQuestionForPlay[];
  surveyQuestion?: QuizSurveyQuestion | null;
  surveySubmitted?: boolean;
  attempt?: Omit<QuizAttemptResult, 'status'> | null;
}

export interface AdminQuizCampaign extends QuizCampaign {
  createdAt: string;
  updatedAt: string;
  sessionCount: number;
  hasAttempts: boolean;
}

export interface QuizPreviewResponse {
  ok: boolean;
  preview: true;
  campaign: AdminQuizCampaign;
  session: QuizSession;
  timerSeconds: number;
  timerMode: QuizTimerMode;
  roundTimerSeconds: number;
  questionTimerSeconds?: number | null;
  questions: QuizQuestionAdmin[];
}

export interface QuizAttemptSubmitResponse {
  ok: boolean;
  attempt: QuizAttemptResult;
  surveyQuestion: QuizSurveyQuestion | null;
  surveySubmitted: boolean;
}

export interface QuizSurveyResultOption {
  index: number;
  label: string;
  count: number;
}

export interface QuizSurveyResponseEntry {
  uid: string;
  memberName: string;
  selectedOption: number;
  selectedLabel: string;
  submittedAt: string;
}

export interface QuizSurveyResults {
  question: QuizSurveyQuestion;
  totalResponses: number;
  options: QuizSurveyResultOption[];
  responses: QuizSurveyResponseEntry[];
}

export interface AdminQuizSessionStats {
  sessionId: string;
  label: string;
  sortOrder: number;
  opensAt: string;
  closesAt: string;
  submissions: number;
  uniqueParticipants: number;
  avgScore: number;
  avgScorePct: number;
  avgDurationSeconds: number;
  topScore: number;
  maxPossibleScore: number;
  completionPct: number;
  firstSubmittedAt: string | null;
  lastSubmittedAt: string | null;
}

export interface AdminQuizTopPerformer {
  rank: number;
  uid: string;
  name: string;
  roundsCompleted: number;
  totalPoints: number;
  maxPoints: number;
  scorePct: number;
  totalDurationSeconds: number;
  earliestSubmittedAt: string;
}

export interface AdminQuizCampaignStats {
  campaignId: string;
  campaignTitle: string;
  audience: QuizCampaignAudience;
  totalRounds: number;
  totalParticipants: number;
  totalAttempts: number;
  totalEligibleParticipants: number;
  participationPct: number;
  averageRoundsCompleted: number;
  averageScorePct: number;
  averageDurationSeconds: number;
  sessionStats: AdminQuizSessionStats[];
  topPerformers: AdminQuizTopPerformer[];
}

export interface BannerConfig {
  title: string;
  description: string;
  linkUrl: string;
  /** CTA button label (defaults to "Learn More") */
  linkText?: string;
  /** Shown above the countdown when countdownEndsAt is set */
  countdownText?: string;
  /** ISO 8601 target; countdown hidden when unset or invalid */
  countdownEndsAt?: string;
  /** Whether the banner is shown on the dashboard (defaults to true when unset) */
  visible?: boolean;
}

export type FeatureKey = 'events' | 'quiz' | 'leaderboard' | 'members' | 'admin';

export type FeatureAllowRole = 'admin' | 'member';

export interface FeatureRule {
  enabled: boolean;
  allowedRoles: FeatureAllowRole[];
  allowedEmails: string[];
}

export type FeaturePolicy = Record<FeatureKey, FeatureRule>;

export type FeatureAccessSnapshot = Record<FeatureKey, boolean>;

export const FEATURE_KEYS: FeatureKey[] = [
  'events',
  'quiz',
  'leaderboard',
  'members',
  'admin',
];
